import { getNodeChain } from '@ui/lib/reactFlow/helpers/getNodeChain'
import { getConnectedEdges, getIncomers, getOutgoers, Node, NodeProps } from 'reactflow'

import { NodeTypeEnum, SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { uuid } from '@ui/lib/utils'
import { useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores'

import { createEdge, createPlaceholderNode } from '../helpers'

export const deleteNode = (node: NodeProps, isDemoMode: boolean = false) => {
  const currentNode = node as unknown as Node
  const { nodes, edges, setNodes, setEdges, setRefreshLayout, setDeletedNodes } = useNodeAndEdgeStore.getState()
  const handlePipelineModified = useSaveUpdateStatusStore.getState().handlePipelineModified

  const nodesToDelete: Node[] = []

  const parentNode = nodes.find((n) => edges.some((e) => e.target === currentNode.id && e.source === n.id))

  const childNode = nodes.find((n) => edges.some((e) => e.source === currentNode.id && e.target === n.id))

  nodesToDelete.push(currentNode)

  // Create new edges
  setEdges((edges) => {
    const incomers = getIncomers(currentNode, nodes, edges)
    const outgoers = getOutgoers(currentNode, nodes, edges)
    const connectedEdges = getConnectedEdges([currentNode], edges)

    const remainingEdges = edges.filter((edge) => !connectedEdges.includes(edge))

    const createdEdges = incomers.flatMap(({ id: source }) => {
      const o = outgoers.map(({ id: target }) => {
        const logEdge = createEdge({
          source,
          target,
          targetHandle: TargetHandleEnum.TARGET_LOGS,
          sourceHandle: SourceHandleEnum.SOURCE_LOGS,
          placeholder: childNode?.type === NodeTypeEnum.PLACEHOLDER,
        })

        const spanEdge = createEdge({
          source,
          target,
          targetHandle: TargetHandleEnum.TARGET_SPANS,
          sourceHandle: SourceHandleEnum.SOURCE_SPANS,
          placeholder: childNode?.type === NodeTypeEnum.PLACEHOLDER,
        })

        const metricsEdge = createEdge({
          source,
          target,
          targetHandle: TargetHandleEnum.TARGET_METRICS,
          sourceHandle: SourceHandleEnum.SOURCE_METRICS,
          placeholder: childNode?.type === NodeTypeEnum.PLACEHOLDER,
        })

        return [logEdge, spanEdge, metricsEdge]
      })

      return o.flat()
    })

    return [...remainingEdges, ...createdEdges]
  })

  // Update the the children of the current node that's being
  // deleted to have the parent of the current node as their parent
  const chain = getNodeChain({ currentNode, nodes, edges })
  chain.shift()
  const updatedChainChildren = chain.map((n) => {
    return {
      ...n,
      data: {
        ...n.data,
        parentSliceId: n.data.parentSliceId === currentNode.id ? parentNode?.id : n.data.parentSliceId,
      },
    }
  })

  // Update the parent node of the current node's children
  setNodes((prevNodes) => {
    return prevNodes
      .filter((node) => node.id !== currentNode.id)
      .map((node) => {
        const updatedNode = updatedChainChildren.find((n) => n.id === node.id)
        if (updatedNode) {
          return updatedNode
        }
        return node
      })
  })

  const parentCurrentEdges = edges.filter((edge) => edge.source === parentNode?.id)

  // For export nodes we want to replace them with a placeholder node only if the parent node
  // does not have more that one child, or is split
  if (node.type === NodeTypeEnum.EXPORT && parentCurrentEdges.length <= 3) {
    createPlaceholderNode({ currentNodeId: parentNode?.id ?? '', placeHolderNodeId: uuid() })
  }

  // For split edges we want to cleanup any hanging placeholder node
  // If the parent node has more than one child, and the child is a placeholder
  // or if the node is attached to a firehose node, delete it
  if (
    (parentCurrentEdges.length > 1 && childNode?.type === NodeTypeEnum.PLACEHOLDER) ||
    (parentNode?.type === NodeTypeEnum.FIREHOSE && !childNode)
  ) {
    setNodes((prevNodes) => {
      let nodes: Node[] = []
      if (!childNode) {
        const firehoseChildNodeIds = edges.filter((edge) => edge.source === parentNode?.id).map((edge) => edge.target)
        // if any of the firehose children are placeholders, delete them
        nodes = prevNodes.filter((node) => {
          if (firehoseChildNodeIds.includes(node.id) && node.type === NodeTypeEnum.PLACEHOLDER) {
            return false
          }
          return true
        })
      } else {
        nodes = prevNodes.filter((node) => node.id !== childNode?.id)
      }

      return nodes
    })
  }

  // We need to check the remaining nodes child nodes of the parent to see if there is only a
  // single placeholder node, if it's deleteable we need to make it undeletable
  const updatedEdges = useNodeAndEdgeStore.getState().edges
  const remainingParentEdges = updatedEdges.filter((edge) => edge && edge.source === parentNode?.id)
  if (remainingParentEdges.length === 1) {
    const remainingChildNode = nodes.find((n) => n.id === remainingParentEdges[0].target)
    if (remainingChildNode?.type === NodeTypeEnum.PLACEHOLDER) {
      setNodes((prevNodes) => {
        return prevNodes.map((node) => {
          if (node.id === remainingChildNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                undeletable: true,
              },
            }
          }
          return node
        })
      })
    }
  }

  if (childNode?.type === NodeTypeEnum.PLACEHOLDER) {
    createPlaceholderNode({ currentNodeId: parentNode?.id ?? '', placeHolderNodeId: uuid() })
  }

  setDeletedNodes(nodesToDelete)

  const updatedNodes = useNodeAndEdgeStore.getState().nodes
  if (!isDemoMode) handlePipelineModified(updatedNodes)

  if (nodes.length === 1) {
    createPlaceholderNode({ currentNodeId: NodeTypeEnum.FIREHOSE, placeHolderNodeId: uuid() })
  }

  setRefreshLayout(true)
}
